import { ecrAlreadyExists } from './validations/ecrAlreadyExists';
import { identityApiRef } from '@backstage/core-plugin-api';
import { JsonObject } from '@backstage/types';
import { repoAlreadyExists } from './validations/repoAlreadyExists';
import { teamAlreadyOnboarded } from './validations/teamAlreadyOnboarded';
import useAsync from 'react-use/esm/useAsync';
import { useUserTeams } from '@internal/ui-common';
import { ValidationResult } from './validations/types';

import { discoveryApiRef, useApi } from '@backstage/core-plugin-api';

type UseValidationsProps = {
  formData: JsonObject;
};

type UseValidationsResult = {
  value: ValidationResult[];
  loading: boolean;
  error: Error | undefined;
};

export const useValidations = (
  props: UseValidationsProps,
): UseValidationsResult => {
  const apiRef = useApi(discoveryApiRef);
  const identityRef = useApi(identityApiRef);
  const { teams } = useUserTeams();

  const { value, loading, error } = useAsync(async () => {
    const results: ValidationResult[] = [];
    const cred = await identityRef.getCredentials();
    const baseUrl = await apiRef.getBaseUrl('custom-proxy');
    if (!cred?.token || !baseUrl) {
      return results;
    }

    // validate if repo already exists or not, if exists then return false
    if (props.formData?.name && props.formData?.repoLocation) {
      const result = await repoAlreadyExists({
        baseUrl: baseUrl,
        token: cred.token,
        repoName: props.formData.name as string,
        repoLocation: props.formData.repoLocation as string,
      });
      results.push(result);
    }

    // validate if ecr already exists or not, if exists then return false
    if (props.formData?.create_ecr || props.formData?.ecr_name) {
      const ecrName = props.formData?.ecr_name ?? props.formData?.name;
      const result = await ecrAlreadyExists({
        baseUrl: baseUrl,
        token: cred.token,
        ecrName: ecrName as string,
      });
      results.push(result);
    }

    // validate if team is already onboarded, if not then return false
    if (props.formData?.owner && teams?.length) {
      const owner = props.formData.owner as string;
      const matchingTeam = teams.find(
        team => `group:${team.namespace}/${team.raw}` === owner,
      );
      const teamSlug = matchingTeam?.teamSlug ?? '';
      const divisionSlug = matchingTeam?.divisionSlug ?? '';

      const result = await teamAlreadyOnboarded({
        baseUrl: baseUrl,
        token: cred.token,
        teamName: owner,
        teamSlug,
        divisionSlug,
      });

      results.push(result);
    }
    return results;
  }, [
    apiRef,
    identityRef,
    teams?.length,
    props.formData?.name,
    props.formData?.repoLocation,
  ]);

  return { value: value ?? [], loading, error };
};
